/**
 * TeamPerformanceTable - Team member performance metrics
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { analyticsApi, type TeamPerformance, type DateRange } from '@services/api';

interface TeamPerformanceTableProps {
  projectId: string;
  dateRange?: DateRange;
}

export default function TeamPerformanceTable({ projectId, dateRange }: TeamPerformanceTableProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<TeamPerformance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof TeamPerformance>('pointsCompleted');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const performanceData = await analyticsApi.getTeamPerformance(projectId, dateRange);
        setData(performanceData);
      } catch (err) {
        setError(t('analytics.teamPerformanceError'));
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [projectId, dateRange, t]);

  const handleSort = (field: keyof TeamPerformance) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return 0;
  });

  const getSortIcon = (field: keyof TeamPerformance) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const getParticipationColor = (rate: number) => {
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      {/* Header */}
      <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
          {t('analytics.teamPerformance')}
        </h3>
        <p className="text-sm text-neutral-500">
          {t('analytics.teamPerformanceDescription')}
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-50 dark:bg-neutral-900">
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                {t('analytics.member')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-900 dark:hover:text-white"
                onClick={() => handleSort('tasksCompleted')}
              >
                <div className="flex items-center gap-1">
                  {t('analytics.tasksCompleted')}
                  {getSortIcon('tasksCompleted')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-900 dark:hover:text-white"
                onClick={() => handleSort('pointsCompleted')}
              >
                <div className="flex items-center gap-1">
                  {t('analytics.pointsCompleted')}
                  {getSortIcon('pointsCompleted')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-900 dark:hover:text-white"
                onClick={() => handleSort('averageCycleTime')}
              >
                <div className="flex items-center gap-1">
                  {t('analytics.avgCycleTime')}
                  {getSortIcon('averageCycleTime')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-900 dark:hover:text-white"
                onClick={() => handleSort('participationRate')}
              >
                <div className="flex items-center gap-1">
                  {t('analytics.participation')}
                  {getSortIcon('participationRate')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {sortedData.map((member) => (
              <tr key={member.userId} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    {member.avatarUrl ? (
                      <img
                        src={member.avatarUrl}
                        alt=""
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                        <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
                          {member.displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-medium text-neutral-900 dark:text-white">
                      {member.displayName}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-neutral-900 dark:text-white">
                    {member.tasksCompleted}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {member.pointsCompleted}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-neutral-900 dark:text-white">
                    {member.averageCycleTime.toFixed(1)} {t('analytics.days')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full max-w-24">
                      <div
                        className={`h-full rounded-full ${getParticipationColor(member.participationRate)}`}
                        style={{ width: `${Math.min(member.participationRate, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm text-neutral-500">
                      {member.participationRate.toFixed(0)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length === 0 && (
        <div className="p-6 text-center text-neutral-500">
          {t('analytics.noTeamData')}
        </div>
      )}
    </div>
  );
}
